import { ShoppingBag } from "lucide-react"
import { Pie, PieChart, Cell } from "recharts"

import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "./card"
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "./chart"

export const description = "A pie chart showing order metrics breakdown"

export function ChartPieLabel({ totalOrders, deliveryCharges, totalDiscount }) {
    // Format currency function for PKR
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0'

        const num = typeof amount === 'string' ? parseFloat(amount.replace(/,/g, '')) : amount

        const formatted = new Intl.NumberFormat('en-PK', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(num)

        return `${formatted}`
    }

    // Create normalized chart data
    // We'll show orders count, delivery charges, and discount as separate slices
    // For better visualization, we'll normalize orders to be comparable with monetary values
    const ordersNormalized = totalOrders * 1000 // Multiply by 1000 for better visual comparison

    const chartData = [
        {
            name: "orders",
            value: ordersNormalized,
            actualValue: totalOrders,
            label: "Total Orders",
            color: "#8b5cf6" // Purple
        },
        {
            name: "delivery",
            value: deliveryCharges,
            actualValue: deliveryCharges,
            label: "Delivery Charges",
            color: "#3b82f6" // Blue
        },
        {
            name: "discount",
            value: Math.abs(totalDiscount),
            actualValue: totalDiscount,
            label: "Discount",
            color: "#ef4444" // Red
        },
    ]

    const chartConfig = {
        orders: {
            label: "Total Orders",
            color: "#8b5cf6",
        },
        delivery: {
            label: "Delivery Charges",
            color: "#3b82f6",
        },
        discount: {
            label: "Discount",
            color: "#ef4444",
        },
    }

    return (
        <Card className="relative overflow-hidden flex flex-col">
            <div className="absolute right-0 top-0 h-16 w-16 bg-purple-500/10 rounded-full -translate-y-8 translate-x-8" />
            <CardHeader className="pb-0">
                <div className="flex items-center justify-between w-full">
                    <CardTitle className="text-sm font-medium">Order Metrics</CardTitle>
                    <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                </div>
                <CardDescription>Order breakdown analysis</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 pb-0">
                <ChartContainer
                    config={chartConfig}
                    className="[&_.recharts-pie-label-text]:fill-foreground mx-auto aspect-square max-h-[250px] pb-0"
                >
                    <PieChart>
                        <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent hideLabel />}
                        />
                        <Pie
                            data={chartData}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            label={({ name, percent, x, y, cx, cy }) => {
                                const item = chartData.find(d => d.name === name)
                                if (!item) return ''

                                const labelText = item.name === 'orders'
                                    ? `${item.actualValue}`
                                    : `${(percent * 100).toFixed(0)}%`

                                return (
                                    <text
                                        x={x}
                                        y={y}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        fontSize={12}
                                        fontWeight="bold"
                                    >
                                        {labelText}
                                    </text>
                                )
                            }}
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                    </PieChart>
                </ChartContainer>
            </CardContent>
            <CardFooter className="flex-col gap-3 text-sm pb-6 pt-3">
                <div className="grid grid-cols-3 gap-4 text-center w-full">
                    <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-[#8b5cf6]"></div>
                            <div className="text-xs text-muted-foreground">Orders</div>
                        </div>
                        <div className="text-lg font-bold">{totalOrders}</div>
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-[#3b82f6]"></div>
                            <div className="text-xs text-muted-foreground">Delivery</div>
                        </div>
                        <div className="text-lg font-bold">{formatCurrency(deliveryCharges)}</div>
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-[#ef4444]"></div>
                            <div className="text-xs text-muted-foreground">Discount</div>
                        </div>
                        <div className="text-lg font-bold text-red-500">-{formatCurrency(totalDiscount)}</div>
                    </div>
                </div>
            </CardFooter>
        </Card>
    )
}